import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, FileText, Info, Loader2, Mic, PenLine, Sparkles, Square, Upload, X } from 'lucide-react';
import { impaginaVerbale } from '../../services/verbaliService';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { TESTO_GENERAZIONE_SUL_SERVER } from './testiAudio';

interface VerbaleAvvioProps {
  eventId: string;
  /** Scelte audio (piano 2): nascoste finché è falso. */
  audioDisponibile: boolean;
  onScriviDaZero: () => void;
  /** Fine dell'impaginazione: il server ha già salvato il contenuto. */
  onImpaginato: (contenuto: string, ultimaModifica: string | null, troncato: boolean) => void;
  /** L'impaginazione prosegue sul server senza la finestra (interrotta, rete caduta o già in corso). */
  onInCorsoSulServer?: () => void;
  /** Impaginazione in corso sul server: le scelte sono disattivate finché dura. */
  bloccato?: boolean;
  onRegistra?: () => void;
  onCaricaAudio?: () => void;
  /**
   * Un audio è già trascritto (piano 2): la prima proposta è «Genera verbale dalla trascrizione»
   * (la pagina passa il pulsante con la sua anteprima, `VerbaleGenera`).
   */
  genera?: React.ReactNode;
}

const MAX_TESTO = 200000;
const INTERROTTO = 'Interrotto, riprova';
/** Stesso testo della generazione dalla trascrizione (un'unica costante in testiAudio.ts). */
export const MESSAGGIO_SUL_SERVER = TESTO_GENERAZIONE_SUL_SERVER;
const ERRORE_SALVATAGGIO = 'Non sono riuscito a salvare il verbale attuale: riprova.';

const PASSI_TESTO = [
  { titolo: 'Porta il testo', nota: 'appunti, bozza o Markdown' },
  { titolo: "L'AI lo impagina", nota: 'nelle sezioni del verbale' },
  { titolo: 'Rivedi', nota: 'sezione per sezione' },
  { titolo: 'Pubblica', nota: 'agli invitati' },
];

const PASSI_AUDIO = [
  { titolo: 'Registra', nota: 'o carica la riunione' },
  { titolo: 'Controlla le voci', nota: 'chi ha parlato' },
  { titolo: 'Rivedi', nota: 'sezione per sezione' },
  { titolo: 'Pubblica', nota: 'agli invitati' },
];

interface SceltaProps {
  icona: React.ReactNode;
  titolo: string;
  nota: string;
  onClick?: () => void;
  evidenza?: boolean;
  disabled?: boolean;
}

const Scelta: React.FC<SceltaProps> = ({ icona, titolo, nota, onClick, evidenza, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`group flex h-full flex-col items-start gap-3 rounded-2xl border p-5 text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:cursor-not-allowed disabled:opacity-50 ${
      disabled ? '' : 'hover:-translate-y-0.5 hover:shadow-md'
    } ${
      evidenza
        ? 'border-emerald-300 bg-gradient-to-br from-emerald-50 to-teal-50 dark:border-emerald-700 dark:from-emerald-900/30 dark:to-teal-900/20'
        : 'border-slate-200 bg-white hover:border-emerald-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-emerald-700'
    }`}
  >
    <span className={`grid h-10 w-10 place-items-center rounded-xl ${
      evidenza ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
    }`}
    >
      {icona}
    </span>
    <span>
      <span className="block text-base font-semibold text-slate-900 dark:text-white">{titolo}</span>
      <span className="mt-1 block text-sm leading-relaxed text-slate-600 dark:text-gray-300">{nota}</span>
    </span>
  </button>
);

interface FinestraImpaginaProps {
  eventId: string;
  onChiudi: () => void;
  /** Fine dell'impaginazione: il server ha già salvato il contenuto. */
  onImpaginato: (contenuto: string, ultimaModifica: string | null, troncato: boolean) => void;
  /** L'impaginazione prosegue sul server senza la finestra (interrotta, rete caduta o già in corso). */
  onInCorsoSulServer?: () => void;
  /** Il verbale ha già un testo: avviso che verrà salvato tra le versioni e sostituito. */
  sostituisce?: boolean;
  /** Prima di impaginare (es. salva le modifiche in sospeso): false ⇒ non si impagina. */
  primaDiImpaginare?: () => Promise<boolean>;
  /** Verbale già pubblicato: il testo impaginato lo vedono subito i lettori. */
  pubblicato?: boolean;
}

/** Avviso delle finestre AI su un verbale pubblicato: il server salva a fine generazione. */
export const AVVISO_GENERAZIONE_PUBBLICATO = 'Il verbale è pubblicato: il testo generato sarà visibile subito ai lettori, appena l’AI finisce';

/**
 * Finestra «Ho già un testo»: il testo grezzo va al server, che lo impagina in streaming
 * (anteprima) e salva il verbale a fine lavoro. Interrotta o con la rete caduta a stream
 * aperto, la generazione prosegue sul server: lo si dice e lo si segnala alla pagina.
 */
export const FinestraImpagina: React.FC<FinestraImpaginaProps> = ({
  eventId,
  onChiudi,
  onImpaginato,
  onInCorsoSulServer,
  sostituisce,
  primaDiImpaginare,
  pubblicato = false,
}) => {
  const [testo, setTesto] = useState('');
  const [anteprima, setAnteprima] = useState('');
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState('');
  const [sulServer, setSulServer] = useState(false);
  const controllerRef = useRef<AbortController | null>(null);
  const anteprimaRef = useRef<HTMLDivElement>(null);

  useEffect(() => () => controllerRef.current?.abort(), []);

  useEffect(() => {
    const el = anteprimaRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [anteprima]);

  const impagina = async () => {
    if (!testo.trim() || inCorso || sulServer) return;
    setErrore('');
    setAnteprima('');
    setInCorso(true);
    if (primaDiImpaginare) {
      let salvato = false;
      try { salvato = await primaDiImpaginare(); } catch { salvato = false; }
      if (!salvato) {
        setErrore(ERRORE_SALVATAGGIO);
        setInCorso(false);
        return;
      }
    }
    const controller = new AbortController();
    controllerRef.current = controller;
    let fatto = false;
    let persa = false;
    let giaInCorso = false;
    await impaginaVerbale(eventId, testo, {
      delta: (pezzo) => setAnteprima((prima) => prima + pezzo),
      connessionePersa: () => { persa = true; },
      rifiutato: (stato, corpo) => {
        if (stato === 409 && corpo?.code === 'IMPAGINAZIONE_IN_CORSO') giaInCorso = true;
      },
      fine: (dati) => {
        fatto = true;
        if (typeof dati?.contenuto === 'string' && dati.contenuto.trim()) {
          onImpaginato(dati.contenuto, dati.ultimaModifica ?? null, dati.troncato === true);
        } else {
          setErrore('Risposta non valida, riprova');
        }
      },
      errore: (messaggio) => {
        fatto = true;
        if (!persa) setErrore(messaggio || INTERROTTO);
      },
    }, controller.signal);
    if (controllerRef.current === controller) controllerRef.current = null;
    const interrotta = !fatto && controller.signal.aborted;
    if (interrotta || persa) {
      setErrore('');
      setSulServer(true);
    }
    if (interrotta || persa || giaInCorso) onInCorsoSulServer?.();
    setInCorso(false);
  };

  const interrompi = () => {
    controllerRef.current?.abort();
  };

  const chiudi = () => {
    if (inCorso) return;
    onChiudi();
  };

  return (
    <div
      className="fixed inset-0 z-[9000] flex items-center justify-center bg-slate-900/60 p-3 backdrop-blur-sm sm:p-6"
      onClick={(e) => { if (e.target === e.currentTarget) chiudi(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Impagina un testo"
        className="flex max-h-full w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-gray-800"
      >
        <div className="flex items-center gap-3 border-b border-slate-200 px-5 py-4 dark:border-gray-700">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
            <Sparkles className="h-4 w-4" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-base font-semibold text-slate-900 dark:text-white">Ho già un testo</p>
            <p className="text-xs text-slate-500 dark:text-gray-400">
              L'AI lo divide nelle sezioni del verbale senza togliere nulla. Il verbale di prima resta tra le versioni.
            </p>
          </div>
          <button
            type="button"
            onClick={chiudi}
            disabled={inCorso}
            aria-label="Chiudi"
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-40 dark:hover:bg-gray-700"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {pubblicato && !sulServer && (
            <div role="note" className="mb-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <span>{AVVISO_GENERAZIONE_PUBBLICATO}</span>
            </div>
          )}
          {sostituisce && !sulServer && (
            <div className="mb-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <span>Il verbale attuale verrà salvato tra le versioni e sostituito</span>
            </div>
          )}
          {inCorso || anteprima ? (
            <div>
              <p className="mb-2 flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-300">
                {inCorso && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                {inCorso ? 'Sto impaginando il verbale…' : 'Anteprima interrotta'}
              </p>
              <div
                ref={anteprimaRef}
                className="testo-ricco h-[50vh] overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 px-6 py-5 font-serif text-slate-800 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-100"
                aria-live="polite"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(anteprima) }}
              />
            </div>
          ) : (
            <div>
              <label htmlFor="verbale-testo-grezzo" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-gray-200">
                Testo della riunione
              </label>
              <textarea
                id="verbale-testo-grezzo"
                value={testo}
                onChange={(e) => setTesto(e.target.value.slice(0, MAX_TESTO))}
                rows={16}
                disabled={sulServer}
                placeholder="Incolla qui appunti, trascrizione o bozza del verbale…"
                className="h-[50vh] w-full resize-none rounded-xl border border-slate-300 bg-white px-4 py-3 font-serif text-base leading-relaxed text-slate-800 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
              />
              <p className="mt-1 text-right text-xs text-slate-400">
                {testo.length.toLocaleString('it-IT')} / {MAX_TESTO.toLocaleString('it-IT')} caratteri
              </p>
            </div>
          )}

          {sulServer && (
            <div role="status" className="mt-3 flex items-start gap-2 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2.5 text-sm text-sky-900 dark:border-sky-800 dark:bg-sky-900/20 dark:text-sky-200">
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <span>{MESSAGGIO_SUL_SERVER}</span>
            </div>
          )}

          {errore && (
            <div role="alert" className="mt-3 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <span>{errore}</span>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3 dark:border-gray-700 dark:bg-gray-900/40">
          {inCorso ? (
            <button
              type="button"
              onClick={interrompi}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              <Square className="h-3.5 w-3.5" aria-hidden="true" />
              Interrompi
            </button>
          ) : sulServer ? (
            <button
              type="button"
              onClick={chiudi}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
            >
              Ho capito
            </button>
          ) : (
            <>
              {anteprima && (
                <button
                  type="button"
                  onClick={() => { setAnteprima(''); setErrore(''); }}
                  className="rounded-lg px-3.5 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Modifica il testo
                </button>
              )}
              <button
                type="button"
                onClick={chiudi}
                className="rounded-lg px-3.5 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={impagina}
                disabled={!testo.trim()}
                className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:from-emerald-700 hover:to-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                {errore ? 'Riprova' : 'Impagina il verbale'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/** Schermata d'avvio del verbale vuoto: guida in 4 passi e scelte per iniziare. */
const VerbaleAvvio: React.FC<VerbaleAvvioProps> = ({
  eventId,
  audioDisponibile,
  onScriviDaZero,
  onImpaginato,
  onInCorsoSulServer,
  bloccato = false,
  onRegistra,
  onCaricaAudio,
  genera,
}) => {
  const [finestra, setFinestra] = useState(false);

  const passi = audioDisponibile ? PASSI_AUDIO : PASSI_TESTO;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-8">
      <div className="mx-auto max-w-3xl">
        <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">Nuovo verbale</p>
        <p className="mt-1 text-base font-semibold text-slate-900 dark:text-white">Come vuoi iniziare?</p>
        <p className="mt-1 text-sm text-slate-600 dark:text-gray-300">
          Il verbale segue la struttura di sempre: intestazione, presenti, ordine del giorno, svolgimento,
          deliberazioni e chiusura. Potrai cambiare tutto prima di pubblicarlo.
        </p>

        <ol className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4" aria-label="Guida in 4 passi">
          {passi.map((p, i) => (
            <li key={p.titolo} className="flex items-start gap-2.5 rounded-xl bg-slate-50 px-3 py-2.5 dark:bg-gray-900/40">
              <span className="grid h-6 w-6 flex-shrink-0 place-items-center rounded-full bg-emerald-600 text-xs font-bold text-white">{i + 1}</span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-slate-800 dark:text-gray-100">{p.titolo}</span>
                <span className="block text-xs text-slate-500 dark:text-gray-400">{p.nota}</span>
              </span>
            </li>
          ))}
        </ol>

        {genera && (
          <div className="mt-6 rounded-2xl border border-emerald-300 bg-gradient-to-br from-emerald-50 to-teal-50 p-5 dark:border-emerald-700 dark:from-emerald-900/30 dark:to-teal-900/20">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl bg-emerald-600 text-white">
                <Sparkles className="h-5 w-5" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className="text-base font-semibold text-slate-900 dark:text-white">La trascrizione è pronta</p>
                <p className="mt-1 text-sm leading-relaxed text-slate-600 dark:text-gray-300">
                  L'AI scrive il verbale completo nelle sezioni di sempre, dalla trascrizione e dalle voci che hai associato.
                  Poi lo rivedi sezione per sezione.
                </p>
              </div>
            </div>
            <div className="mt-4">{genera}</div>
          </div>
        )}

        {genera && (
          <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400">Oppure</p>
        )}
        <div className={`${genera ? 'mt-2' : 'mt-6'} grid gap-3 sm:grid-cols-2`}>
          {audioDisponibile && (
            <>
              <Scelta
                icona={<Mic className="h-5 w-5" aria-hidden="true" />}
                titolo="Registra la riunione"
                nota="Usa il microfono del computer: la trascrizione parte da sola alla fine."
                onClick={onRegistra}
                evidenza={!genera}
                disabled={bloccato}
              />
              <Scelta
                icona={<Upload className="h-5 w-5" aria-hidden="true" />}
                titolo="Carica un file audio"
                nota="m4a, mp3, wav, ogg o webm, fino a 4 ore."
                onClick={onCaricaAudio}
                disabled={bloccato}
              />
            </>
          )}
          <Scelta
            icona={<FileText className="h-5 w-5" aria-hidden="true" />}
            titolo="Ho già un testo"
            nota="Incolla appunti, una bozza o del Markdown: l'AI lo impagina nella struttura del verbale, senza domande."
            onClick={() => setFinestra(true)}
            evidenza={!audioDisponibile}
            disabled={bloccato}
          />
          <Scelta
            icona={<PenLine className="h-5 w-5" aria-hidden="true" />}
            titolo="Scrivo da zero"
            nota="Un foglio con le sezioni già pronte, da riempire."
            onClick={onScriviDaZero}
            disabled={bloccato}
          />
        </div>
      </div>

      {finestra && (
        <FinestraImpagina
          eventId={eventId}
          onChiudi={() => setFinestra(false)}
          onImpaginato={(contenuto, quando, troncato) => {
            setFinestra(false);
            onImpaginato(contenuto, quando, troncato);
          }}
          onInCorsoSulServer={onInCorsoSulServer}
        />
      )}
    </section>
  );
};

export default VerbaleAvvio;
